import { useQuery } from "@tanstack/react-query";
import { ExternalLink, Plus, Settings2, Wifi } from "lucide-react";
import React from "react";
import { Link } from "react-router-dom";

import { Badge } from "@/components/ui/badge/badge";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/context/i18n";
import { cn } from "@/lib/utils";
import { whatsappMetaConfigApi } from "@/pages/whatsapp-meta-config/whatsapp-meta-config.api";

import { InboxEmptyState } from "./inbox-empty-state";

export interface MetaConfigSidebarProps {
  organizationId: string;
  className?: string;
}

export const MetaConfigSidebar: React.FC<MetaConfigSidebarProps> = ({
  organizationId,
  className,
}) => {
  const { t } = useI18n();

  const { data: configs = [], isLoading } = useQuery({
    queryKey: ["whatsapp-meta-configs", organizationId],
    queryFn: () => whatsappMetaConfigApi.list(organizationId, { skipLoading: true }),
    enabled: Boolean(organizationId),
  });

  const count = configs.length;

  return (
    <aside
      className={cn(
        "flex flex-col h-full min-h-0 border-r border-border/80 bg-muted/20",
        className,
      )}
    >
      <div className="shrink-0 px-4 py-3 border-b border-border/80 bg-card/50">
        <div className="flex items-center gap-2 min-w-0">
          <div className="h-8 w-8 rounded-lg bg-emerald-500/15 flex items-center justify-center shrink-0">
            <Settings2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
          </div>
          <div className="min-w-0">
            <h2 className="text-sm font-semibold truncate">
              {t("partnerWhatsappInbox.meta.title")}
            </h2>
            {!isLoading && count > 0 ? (
              <p className="text-[11px] text-muted-foreground">
                {count}{" "}
                {count === 1
                  ? t("partnerWhatsappInbox.meta.integrationOne")
                  : t("partnerWhatsappInbox.meta.integrationMany")}
              </p>
            ) : null}
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto min-h-0 p-3">
        {isLoading ? (
          <div className="space-y-2 animate-pulse">
            {Array.from({ length: 2 }).map((_, i) => (
              <div key={i} className="h-20 rounded-lg bg-muted" />
            ))}
          </div>
        ) : null}

        {!isLoading && configs.length === 0 ? (
          <InboxEmptyState
            compact
            icon={Wifi}
            title={t("partnerWhatsappInbox.meta.emptyTitle")}
            description={t("partnerWhatsappInbox.meta.empty")}
            action={
              <Button
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-700"
                asChild
              >
                <Link to={`/clients/${organizationId}/whatsapp-meta`}>
                  <Plus className="h-4 w-4 mr-1" />
                  {t("partnerWhatsappInbox.meta.new")}
                </Link>
              </Button>
            }
          />
        ) : null}

        {!isLoading && configs.length > 0 ? (
          <ul className="space-y-2">
            {configs.map((c) => (
              <li key={c.id}>
                <div className="rounded-lg border border-border/80 bg-card p-3 shadow-sm hover:border-emerald-500/30 transition-colors">
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-medium text-sm truncate flex-1">
                      {c.label || c.waba_id}
                    </p>
                    <Badge
                      variant="outline"
                      className="text-[10px] px-1.5 py-0 border-emerald-500/30 text-emerald-700 dark:text-emerald-300 shrink-0"
                    >
                      Meta
                    </Badge>
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-1 font-mono truncate">
                    WABA · {c.waba_id}
                  </p>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-0 mt-2 text-emerald-600 dark:text-emerald-400 hover:text-emerald-700"
                    asChild
                  >
                    <Link to={`/clients/${organizationId}/whatsapp-meta/${c.id}`}>
                      <ExternalLink className="h-3.5 w-3.5 mr-1" />
                      {t("partnerWhatsappInbox.meta.open")}
                    </Link>
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </aside>
  );
};
